/**
 * HeWeather "Next" API adapter.
 *
 * The real endpoints (preweatherapi.operapps.com) return a flat, QWeather-v1 style
 * payload (UnitVal{value,unit}, CondV1{code,text}, DayHalfV1, HourlyItemV1, …).
 * This module normalises those responses into the SAME internal shape the rest of
 * the app already consumes (`responses[0].weather[0]` → current / forecast.days /
 * LifeActivityResp / nowcasting / provider), so every component keeps working
 * unchanged. Only the data source differs.
 *
 * Auth model: every endpoint needs `sign` + `ts`, where sign = md5(SIGNKEY + ts + loc).
 * `loc` depends on the endpoint SHAPE (see getJSON); hardcoding it to "0,0" makes
 * every endpoint answer 403 auth failed. SIGNKEY has a built-in debug fallback and
 * can be overridden via VITE_HW_SIGNKEY; VITE_HW_SIGN overrides the whole sign when
 * SIGNKEY is absent.
 *
 * ⚠️ CORS 仅生产构建需要关心：生产 BASE 写死为 `//nextweatherapi.oupeng.com`（跨域直连网关），
 * 网关必须返回 Access-Control-Allow-Origin，否则会 "TypeError: Failed to fetch" 并静默回退 mock
 * （见 deploy/nginx.conf.example 方案 A）。本地 dev / preview 走同源代理，不触发 CORS。
 *
 * Location model: instead of a city id, the /next/* weather endpoints take a
 * `locHandle` = `g.` + base64url(`${lat},${lon}`). We build it directly from the
 * selected place's FRONT-END lat/lon (device geolocation or a user pick), so no
 * hardcoded default city is ever sent; missing coords degrade to "0,0".
 */
import SparkMD5 from 'spark-md5';
const SIGN = import.meta.env.VITE_HW_SIGN || '8c51afcf0d354d308329aedbab7e0545'
const SIGNKEY = import.meta.env.VITE_HW_SIGNKEY || '576d558e527ef8cbbd043345f89f6f7c'
/**
 * BASE —— 按构建目标分流：
 *   - 生产构建（npm run build，import.meta.env.PROD = true）：写死协议相对网关
 *     `//nextweatherapi.oupeng.com`，浏览器跨域直连，因此网关必须返回
 *     Access-Control-Allow-Origin（见 deploy/nginx.conf.example 方案 A）。
 *   - 本地 dev / preview（PROD = false）：用本站 origin，请求打到 /next/*，
 *     由 vite.config.js 的 server.proxy / preview.proxy 反代到网关，绕开 CORS 方便联调。
 */
const BASE = import.meta.env.PROD
  ? '//nextweatherapi.oupeng.com'
  : (typeof location !== 'undefined' ? location.origin : '')

const num = (v, d = 0) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  const f = 10 ** d
  return Math.round(n * f) / f
}
const un = (uv) => (uv && typeof uv === 'object' ? num(uv.value) : num(uv))
const langOf = (locale) => (locale === 'zh-CN' ? 'zh' : 'en')

/**
 * locHandle = g.<base64url(lat,lon)> — verified against the live gateway.
 * Front-end geolocation coordinates are preferred; if none are available we
 * default to "0,0" (num() coerces undefined/NaN to 0) so every interface
 * degrades to a known value instead of a stale/previous-city handle.
 *
 * NOTE: num(v, 4) keeps 4 decimals on purpose. num() defaults to 0 decimals,
 * which silently rounded the coordinates to whole degrees (30.2741,120.1551 →
 * "30,120") — a ~100 km grid, so every request answered for the wrong cell.
 */
export function locHandle(lat = 0, lon = 0) {
  const raw = `${num(lat, 4)},${num(lon, 4)}`
  const b64 = btoa(unescape(encodeURIComponent(raw)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `g.${b64}`
}

async function getJSON(path, params = {}) {
  // ts 时间戳
  const TS = Date.now()
  // sign = md5(SIGNKEY + ts + loc)。loc 必须随端点形状而变（下面 3 种形状已逐个线上实测通过）：
  //   ① 路径型 /next/weather|air|indices|warning/.../{locHandle} → 路径末段的 locHandle
  //   ② 查询型 /next/city/lookup?location=x                      → location 值本身（城市名或 "lon,lat"）
  //   ③ 无位置型 /next/ip/locate、/next/city/top                 → 空串 ""
  // ⚠️ 把 loc 写死成 "0,0" 时以上端点全部返回 403 auth failed（已实测）。
  const LAST = path.split('/').pop() || ''
  const LOC = /^g\./.test(LAST) ? LAST : (params.location != null ? String(params.location) : '')
  // 有 SIGNKEY 就本地计算签名；否则退回 VITE_HW_SIGN（网关放行的调试签名）
  params.sign = SIGNKEY ? SparkMD5.hash(`${SIGNKEY}${TS}${LOC}`) : SIGN
  params.ts = TS

  // BASE 是协议相对地址（"//host"），直接传给 new URL() 会抛 Invalid URL，
  // 必须先补上当前页面协议；非协议相对则照常去尾斜杠。
  const base = BASE.startsWith('//') ? `${location.protocol}${BASE}` : BASE.replace(/\/$/, '')
  const url = new URL(base + path)
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v))
  }
  const res = await fetch(url.toString(), { headers: { Accept: 'application/json' }, credentials: 'omit' })
  if (!res.ok) throw new Error(`HeWeather ${path} → ${res.status}`)
  return res.json()
}

/* ---------------- condition code → OPERA numeric icon ---------------- */
const DAY_ICON = {
  100: 1, 101: 6, 102: 4, 103: 4, 104: 6,
  150: 4, 151: 6, 152: 4, 153: 6,
  300: 12, 301: 12, 302: 15, 303: 12, 304: 15, 305: 12, 306: 12, 307: 12, 308: 15,
  309: 12, 310: 15, 311: 15, 312: 15, 313: 20, 314: 12, 315: 15, 316: 15, 317: 15, 318: 15, 399: 12,
  400: 21, 401: 21, 402: 21, 403: 21, 404: 20, 405: 20, 406: 20, 407: 21, 408: 21, 409: 21, 410: 21, 499: 21,
  500: 26, 501: 26, 502: 26, 503: 6, 504: 6, 507: 6, 508: 6, 509: 26, 510: 26, 511: 6,
  900: 1, 901: 6, 999: 6
}
const NIGHT_ICON = {
  100: 29, 101: 7, 102: 30, 103: 33, 104: 8,
  150: 30, 151: 7, 152: 30, 153: 8,
  300: 12, 301: 12, 302: 15, 303: 12, 304: 15, 305: 12, 306: 12, 307: 12, 308: 15,
  309: 12, 310: 15, 311: 15, 312: 15, 313: 20, 314: 12, 315: 15, 316: 15, 317: 15, 318: 15, 399: 12,
  400: 21, 401: 21, 402: 21, 403: 21, 404: 20, 405: 20, 406: 20, 407: 21, 408: 21, 409: 21, 410: 21, 499: 21,
  500: 26, 501: 26, 502: 26, 503: 6, 504: 6, 507: 6, 508: 6, 509: 26, 510: 26, 511: 6,
  900: 29, 901: 6, 999: 6
}
const OPERAIcon = (heCode, isNight) => {
  const c = Number(heCode)
  const map = isNight ? NIGHT_ICON : DAY_ICON
  return map[c] || (isNight ? 6 : 4)
}
const SYMBOL_DAY = ['d000', 'd100', 'd200', 'd240', 'd300', 'd400']
const symbolFor = (icon, isNight) => {
  if (isNight) return [29, 30, 31, 32, 33, 34].includes(icon) ? 'n100' : 'n100'
  return SYMBOL_DAY[icon % SYMBOL_DAY.length]
}

/* ---------------- wind / humidity helpers ---------------- */
const DIRS = ['北风', '东北风', '东风', '东南风', '南风', '西南风', '西风', '西北风']
const dirName = (deg) => {
  const i = Math.round(((Number(deg) || 0) % 360) / 45) % 8
  return DIRS[i]
}
const compassName = (compass) => ({
  n: '北风', nne: '东北风', ne: '东北风', ene: '东北风', e: '东风', ese: '东南风', se: '东南风', sse: '东南风',
  s: '南风', ssw: '西南风', sw: '西南风', wsw: '西南风', w: '西风', wnw: '西北风', nw: '西北风', nnw: '西北风'
}[String(compass).toLowerCase()] || '')
const ms2kmh = (v) => num(un(v) * 3.6)
const m2km = (v) => num(un(v) / 1000, 1)

/* ---------------- moon phase (HeWeather → OPERA) ---------------- */
const MOON = {
  'new-moon': { zh: '新月', code: 'NewM' },
  'waxing-crescent': { zh: '娥眉月', code: 'WxCc' },
  'first-quarter': { zh: '上弦月', code: 'FQtr' },
  'waxing-gibbous': { zh: '盈凸月', code: 'WxGb' },
  'full-moon': { zh: '满月', code: 'FullM' },
  'waning-gibbous': { zh: '亏凸月', code: 'WnGb' },
  'last-quarter': { zh: '下弦月', code: 'LQtr' },
  'waning-crescent': { zh: '残月', code: 'WnCr' }
}

/* ---------------- UV / AQI level text ---------------- */
const uvDescOf = (uv) => {
  const v = num(uv)
  if (v <= 2) return '低'
  if (v <= 5) return '中'
  if (v <= 7) return '高'
  if (v <= 10) return '很高'
  return '极高'
}
const aqiInfo = (aqi) => {
  const v = num(aqi)
  if (v <= 50) return { severity: '优', level: 1 }
  if (v <= 100) return { severity: '良', level: 2 }
  if (v <= 150) return { severity: '轻度污染', level: 3 }
  if (v <= 200) return { severity: '中度污染', level: 4 }
  if (v <= 300) return { severity: '重度污染', level: 5 }
  return { severity: '严重污染', level: 6 }
}

/* ---------------- 生活指数 (real /next/indices) ---------------- */
// 在「生活指数」模块中展示的实时指数类型（QWeather 类型 ID）。
// 只保留产品要求的 5 项：运动 / 洗车 / 穿衣 / 钓鱼 / 紫外线。
const LIFE_TYPES = [1, 2, 3, 4, 5] // 运动 / 洗车 / 穿衣 / 钓鱼 / 紫外线
const LIFE_ORDER = LIFE_TYPES

// 自然灾害预警等级配色（兼容 severityColor 与 severity 两种字段命名）
const SEV_COLOR = {
  Blue: '#2b6cff', Yellow: '#e0a106', Orange: '#ff7a1a', Red: '#e53935',
  Minor: '#2b6cff', Moderate: '#e0a106', Severe: '#ff7a1a', Extreme: '#e53935'
}
const sevColorOf = (w) => SEV_COLOR[w?.severityColor] || SEV_COLOR[w?.severity] || '#7a8699'

/**
 * 归一化 /next/indices/{period} 的返回。原始 `daily` 是「类型 × 日期」的扁平数组，
 * 这里按日期分组，并映射为内部统一结构 { type,name,level,category,text }。
 */
function buildLifeDays(dayItems, indicesRaw) {
  const byDate = {}
  ;(indicesRaw?.daily || []).forEach((it) => {
    const d = it.date
    if (!byDate[d]) byDate[d] = []
    byDate[d].push({
      type: Number(it.type),
      name: it.name || '',
      level: it.level || '',
      category: it.category || '',
      text: it.text || ''
    })
  })
  return dayItems.map((d) => {
    const date = String(d.daily.valid).slice(0, 10)
    // 只保留产品要求的 5 类指数，避免接口多返回的类型漏到 UI 上
    const items = (byDate[date] || [])
      .filter((it) => LIFE_ORDER.includes(Number(it.type)))
      .sort((a, b) => LIFE_ORDER.indexOf(a.type) - LIFE_ORDER.indexOf(b.type))
    return {
      valid: d.daily.valid,
      dailySymbol: d.daily.symbol,
      tempHigh: d.daily.tempHi,
      tempLow: d.daily.tempLo,
      lifeDailyIndices: items,
      lifeTips: []
    }
  })
}

/** 从 /next/warning/now 的多种可能字段命名中，稳健地抽出预警数组。 */
function extractWarningArray(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return raw.warning || raw.warnings || raw.list || raw.data || raw.alerts || []
}
function normalizeWarnings(raw) {
  return extractWarningArray(raw).map((w, i) => ({
    id: w.id || `${w.type || ''}-${w.pubTime || w.startTime || i}`,
    type: w.type || '',
    typeName: w.typeName || w.typename || w.name || '',
    severity: w.severity || '',
    severityColor: w.severityColor || w.severity || '',
    color: sevColorOf(w),
    title: w.title || '',
    text: w.text || w.content || w.desc || '',
    pubTime: w.pubTime || '',
    startTime: w.startTime || '',
    endTime: w.endTime || ''
  }))
}

/* ---------------- normalisers ---------------- */
function isNightHour(h) { return h < 6 || h >= 19 }
const hourOf = (iso) => new Date(iso).getHours()

/**
 * 从接口 ISO 时间（含城市时区偏移，如 2026-09-17T07:00-04:00）提取城市本地
 * 墙钟时间分量。接口返回的字符串里，偏移之前那段 `YYYY-MM-DDTHH:MM` 就是该城市的
 * 本地时间；若仍用 new Date(iso).getHours() 会换算到「浏览器所在时区」，跨时区城市
 * （如纽约 -04:00）整点会被错算约 12 小时。这里直接按字符串解析，绕开时区转换。
 */
export function localPartsOf(iso) {
  const m = String(iso || '').match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (!m) return null
  return { y: +m[1], mo: +m[2], d: +m[3], hh: +m[4], mm: +m[5] }
}

function buildCurrent(now, air, lang) {
  const c = now || {}
  const cond = c.condition || {}
  const wind = c.wind || {}
  const h = num((c.humidity || 0) * 100)
  const lpNow = localPartsOf(c.updatedAt)
  const hrNow = lpNow ? lpNow.hh : hourOf(c.updatedAt || new Date().toISOString())
  const isNight = isNightHour(hrNow)
  const icon = OPERAIcon(cond.code, isNight)
  const aqi = air?.indexes?.[0]?.aqi ?? null
  const ai = aqiInfo(aqi ?? 0)
  return {
    baro: un(c.pressure),
    cap: cond.text || '—',
    capAbbr: cond.text || '—',
    daytime: isNight ? 'n' : 'd',
    dewPt: un(c.dewPoint),
    feels: un(c.feelsLike),
    rh: h,
    icon,
    symbol: symbolFor(icon, isNight),
    pvdrIcon: String(icon),
    urlIcon: '',
    wx: cond.code || '',
    sky: '',
    temp: un(c.temperature),
    tempD: num(un(c.temperature) * 9 / 5 + 32, 1),
    tempDesc: 9,
    utci: un(c.feelsLike),
    uv: num(c.uvIndex),
    uvDesc: uvDescOf(c.uvIndex),
    vis: m2km(c.visibility),
    windDir: num(wind.degree),
    windSpd: ms2kmh(wind.speed),
    windTh: 22,
    windGust: ms2kmh(c.windGust || wind.speed),
    created: c.updatedAt || new Date().toISOString(),
    pvdrCap: cond.text || '—',
    pvdrWindDir: compassName(wind.compass) || dirName(wind.degree),
    pvdrWindSpd: `${num(wind.scale)}级`,
    aqi: aqi ?? 0,
    aqiSeverity: aqi != null ? ai.severity : '—',
    aqLevel: aqi != null ? ai.level : 0,
    primaryPollutant: air?.indexes?.[0]?.primaryPollutant?.name
      ? `${air.indexes[0].primaryPollutant.name} ${air.indexes[0].primaryPollutant.fullName || ''}`.trim()
      : '—',
    aqiValidTime: c.updatedAt || new Date().toISOString(),
    cloudCover: num((c.cloudCover || 0) * 100),
    richCaps: [],
    precip: num((c.precipitation?.amount?.value || 0) * 10) / 10
  }
}

function buildDaily(day) {
  const dt = day.daytime || {}
  const nt = day.nighttime || {}
  const dCond = dt.condition || {}
  const dWind = dt.wind || {}
  const icon = OPERAIcon(dCond.code, false)
  const precipProb = Math.max(
    num((dt.precipitation?.probability || 0) * 100),
    num((nt.precipitation?.probability || 0) * 100)
  )
  const a = day.astro || {}
  const moon = MOON[String(a.moonPhase).toLowerCase()] || { zh: '—', code: '' }
  return {
    daily: {
      valid: day.forecastStartTime,
      day: { cap: dCond.text || '—', pvdrCap: dCond.text || '—', icon, symbol: symbolFor(icon, false), pvdrIcon: String(icon), windDir: num(dWind.degree), windSpd: ms2kmh(dWind.speed), precip: precipProb },
      night: { cap: (nt.condition?.text) || '—', pvdrCap: (nt.condition?.text) || '—', icon: OPERAIcon(nt.condition?.code, true), symbol: 'n100', pvdrIcon: String(OPERAIcon(nt.condition?.code, true)), windDir: num((nt.wind || {}).degree), windSpd: ms2kmh((nt.wind || {}).speed), precip: precipProb },
      pvdrCap: dCond.text || '—',
      pvdrWindDir: compassName(dWind.compass) || dirName(dWind.degree),
      pvdrWindSpd: `${num(dWind.scale)}级`,
      icon,
      symbol: symbolFor(icon, false),
      pvdrIcon: String(icon),
      iconUrl: '',
      precip: precipProb,
      windMax: Math.max(num(dWind.scale), num((nt.wind || {}).scale)),
      windMaxDir: num(dWind.degree),
      windTh: 22,
      rhHi: num((dt.humidity || 0) * 100),
      rhLo: num((nt.humidity || 0) * 100),
      rh: num((dt.humidity || 0) * 100),
      tempHi: num(un(day.temperatureMax)),
      tempLo: num(un(day.temperatureMin)),
      tempHiD: num(un(day.temperatureMax) * 9 / 5 + 32),
      tempLoD: num(un(day.temperatureMin) * 9 / 5 + 32),
      uv: num(day.uvIndexMax),
      uvDesc: uvDescOf(day.uvIndexMax),
      created: day.forecastStartTime,
      rainAmount: num((dt.precipitation?.amount?.value || 0) + (nt.precipitation?.amount?.value || 0), 1),
      snowAmount: 0,
      raToMN: 0, saToMN: 0,
      feelsHi: num(un(day.temperatureMax) + 3),
      feelsLo: num(un(day.temperatureMin) + 2),
      vis: 10,
      // 日接口不提供露点/气压，这里先给兜底值，随后用当天逐小时均值覆盖（见 fetchOverviewLive）
      dewPt: num(un(day.temperatureMin) - 6),
      baro: 1014,
      // 日摘要的风速文案是 km/h，而日接口只给蒲福风级（wind.scale），所以另存 km/h 口径：
      // 平均风速先取日间值，稍后由逐小时均值覆盖；夜间最大阵风直接取接口 nighttime.windGustMax
      windAvgKmh: ms2kmh(dWind.speed),
      gustNightKmh: ms2kmh(nt.windGustMax || dt.windGustMax),
      aqi: 0
    },
    almanac: {
      valid: day.forecastStartTime,
      sunrise: a.sunrise || null,
      sunset: a.sunset || null,
      moonrise: a.moonrise || null,
      moonset: a.moonset || null,
      moonPhase: moon.zh,
      moonPhaseCode: moon.code
    },
    hourly: []
  }
}

function buildHour(h, aqi) {
  const cond = h.condition || {}
  const wind = h.wind || {}
  const lp = localPartsOf(h.forecastTime)
  const hr = lp ? lp.hh : hourOf(h.forecastTime)
  const mm = lp ? lp.mm : 0
  const isNight = isNightHour(hr)
  const icon = OPERAIcon(cond.code, isNight)
  const type = (h.precipitation?.type || 'none')
  const amt = num(h.precipitation?.amount?.value || 0, 1)
  const rain = type === 'snow' ? 0 : amt
  const snow = type === 'snow' ? amt : 0
  const prob = num((h.precipitation?.probability || 0) * 100)
  const rh = num((h.humidity || 0) * 100)
  const temp = un(h.temperature)
  // 露点优先取接口 dewPoint（实测 227/240 个点位与气温不同）；仅在字段缺失时退回经验公式
  const dewPt = h.dewPoint ? un(h.dewPoint) : num(temp - (100 - rh) / 5)
  return {
    valid: h.forecastTime,
    hour: `${String(hr).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
    cap: cond.text || '—',
    pvdrCap: cond.text || '—',
    pvdrWindDir: compassName(wind.compass) || dirName(wind.degree),
    pvdrWindSpd: `${num(wind.scale)}级`,
    icon,
    symbol: symbolFor(icon, isNight),
    pvdrIcon: String(icon),
    wx: cond.code || '',
    sky: '',
    temp,
    // 体感取接口 feelsLike（实测 227/240 个点位与气温不同）；缺失才退回气温
    feels: h.feelsLike ? un(h.feelsLike) : temp,
    precip: prob,
    rh,
    windSpd: ms2kmh(wind.speed),
    windDir: num(wind.degree),
    // 阵风在接口的顶层 windGust，不在 wind 对象内（wind 只有 degree/compass/speed/scale）；
    // 缺失才退回风速
    windGust: ms2kmh(h.windGust || wind.speed),
    // UV 直接取接口逐小时 uvIndex（白天 8-17 点有真实值），不再用日照钟形估算
    uv: num(h.uvIndex),
    // 能见度：接口返回 visibility（单位 m），换算为公里；缺失时退回 10km
    vis: h.visibility ? m2km(h.visibility) : 10,
    baro: un(h.pressure),
    dewPt,
    cloudCover: num((h.cloudCover || 0) * 100),
    rainAmount: rain,
    snowAmount: snow,
    raAccu: 0, saAccu: 0,
    aqi: aqi ?? 0
  }
}

/* ---------------- public: city search ---------------- */

/**
 * Live city search via /next/city/lookup (QWeather geocoding).
 * Returns the SAME {id,name,region,country,lat,lon,type} shape the
 * LocationPicker already renders, so no UI changes are needed. The endpoint
 * even echoes back a valid locHandle, but we rebuild it from lat/lon anyway so
 * device-geolocation and preset places stay on the exact same code path.
 */
export async function fetchCityLookup(q, locale = 'zh-CN') {
  const data = await getJSON('/next/city/lookup', { location: q, lang: langOf(locale) })
  if (!data || data.code !== '200') return []
  return (data.location || []).map((c) => {
    const name = c.name || ''
    // Prefer the prefecture-level city (adm2) as the region so the label reads
    // "大同市 平城" / "北京市 海淀区"; fall back to province (adm1) for city-level hits.
    const region = (c.adm2 && c.adm2 !== name ? c.adm2 : c.adm1) || ''
    return {
      id: c.id || `${c.lat},${c.lon}`,
      name,
      region,
      country: c.countryCode || c.country || '',
      lat: Number(c.lat),
      lon: Number(c.lon),
      type: c.type || 'city',
      locHandle: c.locHandle || null
    }
  })
}

/**
 * Reverse-geocode a lat/lon pair into a real city name. QWeather's lookup accepts
 * `location=lon,lat`; we return the nearest match in the same normalized shape as
 * fetchCityLookup. Used for device-GPS and IP coordinates so the UI always shows a
 * real city (e.g. "大同市 平城区") instead of a bare "当前位置".
 */
export async function reverseGeocode(lat, lon, locale = 'zh-CN') {
  const items = await fetchCityLookup(`${lon},${lat}`, locale)
  return items && items.length ? items[0] : null
}

/**
 * Detect the user's CURRENT city WITHOUT a permission prompt, via IP geolocation
 * (/next/ip/locate) + reverse geocode. Returns the same shape the rest of the app
 * consumes.
 *
 * NOTE: intentionally NOT used for auto-fill any more. IP geolocation reflects
 * the exit IP (VPN / office / server), so it fabricated a "default" city the user
 * never chose (e.g. showing 郑州). The app now auto-fills from FRONT-END device
 * coordinates only, and degrades to locHandle "0,0" when none are available.
 * Kept as a standalone helper for callers that explicitly want IP location.
 */
export async function fetchCurrentCity(locale = 'zh-CN') {
  const ip = await getJSON('/next/ip/locate')
  if (!ip || ip.lat == null || ip.lon == null) return null
  const coords = `${ip.lon},${ip.lat}`
  const data = await getJSON('/next/city/lookup', { location: coords, lang: langOf(locale) })
  const c = data?.location?.[0]
  if (!c) return null
  const name = c.name || ''
  const region = (c.adm2 && c.adm2 !== name ? c.adm2 : c.adm1) || ''
  const lat = Number(c.lat ?? ip.lat)
  const lon = Number(c.lon ?? ip.lon)
  return {
    id: c.id || `${lat},${lon}`,
    name,
    region,
    country: c.countryCode || c.country || 'cn',
    lat,
    lon,
    type: 'geo',
    locHandle: c.locHandle || locHandle(lat, lon)
  }
}

/* ---------------- public: fetch + normalise ---------------- */
export async function fetchOverviewLive({ lat, lon, lang = 'zh-CN' } = {}) {
  // Prefer front-end coordinates; num() coerces missing/NaN/undefined to 0 so the
  // locHandle degrades to "0,0" instead of a stale, invented location.
  // 4 位小数：num() 默认取整会把坐标压成整数度，请求就落到错误的网格上
  const la = num(lat, 4)
  const lo = num(lon, 4)
  const lh = locHandle(la, lo)
  const l = langOf(lang)
  const [now, daily, hourly, air, indicesRaw, warnRaw] = await Promise.all([
    getJSON(`/next/weather/now/${lh}`, { lang: l }),
    getJSON(`/next/weather/daily/${lh}`, { lang: l, days: 10 }),
    // 逐小时接口最多支持 240 小时（10 天）——与日预报的 10 天对齐，否则图表只能滑到前 4 天
    getJSON(`/next/weather/hourly/${lh}`, { lang: l, hours: 240 }),
    getJSON(`/next/air/now/${lh}`, { lang: l }).catch(() => null),
    getJSON(`/next/indices/3d/${lh}`, { lang: l, type: LIFE_TYPES.join(',') }).catch(() => null),
    getJSON(`/next/warning/now/${lh}`, { lang: l }).catch(() => null)
  ])

  const airAqi = air?.indexes?.[0]?.aqi ?? 0
  // let（非 const）：下方会按「今天」重新对齐 day0 的顺序，可能裁掉开头的过期日
  let dayItems = (daily?.days || []).map(buildDaily)
  const byDate = {}
  dayItems.forEach((d, i) => { byDate[String(d.daily.valid).slice(0, 10)] = i })

  ;(hourly?.hours || []).forEach((h) => {
    const di = byDate[String(h.forecastTime).slice(0, 10)]
    if (di != null) dayItems[di].hourly.push(buildHour(h, airAqi))
  })

  // 部分语言（如 zh）的日预报接口会把 day0 误设为「昨天」，而 hourly 数据自今天起，
  // 于是全部落到 day1 之后 —— 首页 hourly 图表只认 day0.hourly 就会显示空白
  // （纽约在中文下就是这个表现，英文正常）。以逐小时首条记录的日期为锚，把「今天」
  // 那一天旋转到 day0；开头的过期日（昨天及更早）直接裁掉，不影响后续聚合与展示。
  const firstHourDate = (hourly?.hours || [])[0]?.forecastTime?.slice(0, 10)
  if (firstHourDate != null) {
    const anchor = byDate[firstHourDate]
    if (anchor != null && anchor > 0) dayItems = dayItems.slice(anchor)
  }
  // 逐小时的露点/气压/UV/体感/阵风已全部取自接口真实字段（见 buildHour），
  // 这里只把「日接口缺失、但逐小时有」的量聚合回日粒度，供日摘要与月报使用。
  dayItems.forEach((d) => {
    d.daily.aqi = airAqi
    if (!d.hourly.length) return
    const n = d.hourly.length
    const avg = (pick, dp = 1) => num(d.hourly.reduce((a, h) => a + (Number(pick(h)) || 0), 0) / n, dp)
    // 日接口不含 visibility / pressure / dewPoint，用当天逐小时均值回填（月报、日摘要会用到）
    d.daily.vis = avg((h) => h.vis)
    d.daily.baro = avg((h) => h.baro)
    d.daily.dewPt = avg((h) => h.dewPt)
    // 日摘要的风速文案是 km/h，而日接口只给蒲福风级（wind.scale），故按逐小时真实风速取平均
    d.daily.windAvgKmh = avg((h) => h.windSpd, 0)
  })

  const current = buildCurrent(now, air, l)
  const lifeDays = buildLifeDays(dayItems, indicesRaw)
  const warnings = normalizeWarnings(warnRaw)

  return {
    responses: [
      {
        source: {
          id: 'hw',
          coordinates: { lat: la, lon: lo },
          location: { Name: '', StateCode: '', CountryName: 'china', CountryCode: 'CN', TimezoneName: 'Asia/Shanghai', TimezoneOffset: '08:00:00' },
          utcOffset: '08:00:00',
          countryCode: 'CN'
        },
        weather: [
          {
            alerts: [],
            current,
            forecast: { days: dayItems },
            nowcasting: null,
            LifeActivityResp: { days: lifeDays },
            warnings,
            provider: { name: '和风天气 (HeWeather)', url: 'https://www.qweather.com/', gridEnabled: 1 },
            aqiProvider: { name: '和风天气 (HeWeather)' },
            insights: { insights: [] }
          }
        ]
      }
    ],
    units: { system: 'Metric', pressure: 'hPa', temperature: '°C', speed: 'km/h', height: 'mm', distance: 'km', time: 's' },
    userProfile: { used: false },
    copyright: 'Data © HeWeather / operapps'
  }
}
